// hooks/useInterviewHandlers.js
import { useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from '../../../components/Common/Toast';
import Swal from 'sweetalert2';
import config from '../../../config';
import io from 'socket.io-client';

export const useInterviewHandlers = (
  token,
  user,
  selectedTeam,
  activeInterview,
  setActiveInterview,
  setIsInterviewActive,
  setInterviewQuestions,
  setCurrentQuestion,
  setCurrentQuestionIndex,
  setInterviewStartTime,
  setCandidateProgress,
  setCurrentInterviewForCandidate,
  setShowCandidateQuestions,
  setWaitingForNext,
  setNextQuestionTime,
  setInterviewCompleted,
  setPendingInterview,
  setShowInterviewConfirm,
  setShowAddMemberOptions,
  setShowInterviewModal,
  activeInterviewIdRef,
  isInterviewEndedByAdminRef,
  askedQuestionsRef,
  timers,
  availableQuestions,
  setAvailableQuestions,
  isAdmin,
  isCheckingInterview,
  setIsCheckingInterview,
  showCandidateQuestions,
  showInterviewConfirm,
  waitingForNext,
  interviewCompleted,
  isInterviewActive,
  interviewQuestions,
  currentQuestionIndex,
  currentQuestion,
  currentInterviewForCandidate,
  fetchTeamMembers,
  setIsStartingInterview,
  setLoading,
  isStartingInterview,
  setAskedQuestionIds,
  mountedRef,
  setCurrentRound,
  totalRounds,
  setCompletedRounds,
  completedRounds,
  setAnsweredQuestions,
  setTeams,
  setSelectedTeam
) => {
  // ── Clear all interview timers ─────────────────────────────────
  const clearAllTimers = () => {
    ['first', 'interval', 'candidate', 'candidateInterval'].forEach(key => {
      if (timers.current[key]) {
        if (key.includes('interval')) clearInterval(timers.current[key]);
        else clearTimeout(timers.current[key]);
      }
    });
  };

  // ── Reset interview state ─────────────────────────────────────
  const resetInterviewState = useCallback(() => {
    clearAllTimers();
    setActiveInterview(null);
    setIsInterviewActive(false);
    setInterviewQuestions([]);
    setCurrentQuestion(null);
    setCurrentQuestionIndex(0);
    setCandidateProgress(null);
    activeInterviewIdRef.current = null;
  }, [setActiveInterview, setIsInterviewActive, setInterviewQuestions,
      setCurrentQuestion, setCurrentQuestionIndex, setCandidateProgress,
      activeInterviewIdRef]);

  // ── Load available questions (unused in multi-candidate but kept for compatibility) ──
  const loadAvailableQuestions = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${config.baseURL}/api/teams/interview/questions`,
        { params: { limit: 20 }, headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.success && setAvailableQuestions) {
        setAvailableQuestions(Object.values(data.questions).flatMap(r => r.questions || []));
      }
    } catch (err) {
      console.error('Error loading questions:', err);
    }
  }, [token, setAvailableQuestions]);

  // ── Check active interview for team (admin view) ─────────────
  const checkActiveInterview = useCallback(async () => {
    if (!selectedTeam?.team_id) return;
    try {
      const { data } = await axios.get(
        `${config.baseURL}/api/teams/${selectedTeam.team_id}/active-interview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.success && data?.active_interview) {
        setActiveInterview(data.active_interview);
        setIsInterviewActive(true);
        setInterviewStartTime(data.active_interview.start_time);
        activeInterviewIdRef.current = data.active_interview.interview_id;
        if (isAdmin) {
          setCandidateProgress({
            candidate_name: data.active_interview.candidate_name || 'Candidate',
            status: 'in_progress',
            current_question: data.active_interview.answered_count || 0,
            total_questions: data.active_interview.total_questions || 0,
          });
        }
      }
    } catch (err) {
      console.error('Error checking active interview:', err);
    }
  }, [selectedTeam, token, setActiveInterview, setIsInterviewActive,
      setInterviewStartTime, activeInterviewIdRef, isAdmin, setCandidateProgress]);

  // ── Check if user (candidate) has an active interview ─────────
  const checkIfUserInActiveInterview = useCallback(async () => {
    if (!setIsCheckingInterview || isCheckingInterview) return;
    setIsCheckingInterview(true);
    try {
      const { data } = await axios.get(
        `${config.baseURL}/api/teams/user/active-interview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (
        data?.success &&
        data?.active_interview &&
        !showCandidateQuestions &&
        !showInterviewConfirm &&
        !waitingForNext &&
        !interviewCompleted &&
        !currentInterviewForCandidate
      ) {
        setPendingInterview(data.active_interview);
        setShowInterviewConfirm(true);
      }
    } catch (err) {
      console.error('Error checking user active interview:', err);
    } finally {
      setIsCheckingInterview(false);
    }
  }, [token, setIsCheckingInterview, isCheckingInterview, showCandidateQuestions,
      showInterviewConfirm, waitingForNext, interviewCompleted,
      currentInterviewForCandidate, setPendingInterview, setShowInterviewConfirm]);

  // ── Get next question for a specific candidate ───────────────
  const getNextQuestion = useCallback(async (interviewId, candidateId) => {
    try {
      const { data } = await axios.get(
        `${config.baseURL}/api/teams/interview/${interviewId}/next-question`,
        { params: { candidateId }, headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    } catch (err) {
      console.error('Error getting next question:', err);
      return null;
    }
  }, [token]);

  // ── Ask candidate question (unused in group flow, kept for backward compat) ──
  const askCandidateQuestion = useCallback(async () => {
    // For group mode this is not called; kept as a no‑op
    return null;
  }, []);

  // ── Log candidate question ───────────────────────────────────
  const logCandidateQuestion = useCallback(async (
    interviewId, candidateId, questionId, action = 'answered', answerText = ''
  ) => {
    try {
      const payload = {
        asked_by_user_id: user?.id,
        candidateId,
        question_id: questionId,
        question_category: 'general',
        action,
        ...(answerText && action === 'answered' ? { answer_text: answerText } : {}),
      };
      const { data } = await axios.post(
        `${config.baseURL}/api/teams/interview/${interviewId}/log-question`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.success) {
        toast.success(action === 'answered' ? 'Response saved' : 'Question skipped');
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error logging question:', err);
      toast.error(`Failed to ${action} question`);
      return false;
    }
  }, [user, token]);

  // ── Start interview (multi‑candidate) ────────────────────────
  const handleStartInterview = useCallback(async (interviewConfig) => {
    if (isStartingInterview || !selectedTeam || isInterviewActive) {
      if (!selectedTeam) toast.error('No team selected');
      if (isInterviewActive) toast.error('An interview is already in progress');
      return;
    }
    setIsStartingInterview(true);
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${config.baseURL}/api/teams/${selectedTeam.team_id}/start-interview`,
        {
          mode: interviewConfig.mode,
          capture: interviewConfig.capture || ['camera'],
          candidates: interviewConfig.candidates,           // array of user IDs
          candidate_name: interviewConfig.candidate_name,    // legacy, can be ignored
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.success) {
        toast.success('Interview started successfully');
        setActiveInterview(data.interview);
        activeInterviewIdRef.current = data.interview.interview_id;
        setInterviewQuestions(data.interview.questions || []);
        setIsInterviewActive(true);
        setInterviewStartTime(new Date().toISOString());
        setCurrentQuestionIndex(0);
        setShowInterviewModal(false);
        setShowAddMemberOptions(false);
        // Do not set candidateProgress globally – now managed per candidate in video component
        return data;
      }
      throw new Error(data.message);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to start interview');
      throw err;
    } finally {
      setLoading(false);
      setIsStartingInterview(false);
    }
  }, [isStartingInterview, selectedTeam, isInterviewActive, token, setLoading,
      setActiveInterview, activeInterviewIdRef, setInterviewQuestions,
      setIsInterviewActive, setInterviewStartTime, setCurrentQuestionIndex,
      setShowInterviewModal, setShowAddMemberOptions]);

  // ── End interview for a specific candidate ───────────────────
  const handleEndInterview = useCallback(async (interviewId, candidateId, decision, addToTeam) => {
    try {
      const { data } = await axios.post(
        `${config.baseURL}/api/teams/interview/${interviewId}/end`,
        { candidateId, decision, add_to_team: addToTeam },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.success) {
        if (data.team_member_added) {
          toast.success('Candidate added to team!');
          window.dispatchEvent(new CustomEvent('refreshTeamMembers', {
            detail: { teamId: selectedTeam?.team_id }
          }));
        } else if (decision === 'rejected') {
          toast.info('Candidate rejected');
        } else {
          toast.success('Interview ended for candidate');
        }
        return true;
      }
      return false;
    } catch (err) {
      if (err.response?.data?.message === 'Interview is not active') {
        toast.info('Interview had already ended');
      } else {
        toast.error('Failed to end interview');
      }
      return false;
    }
  }, [token, selectedTeam]);

  // ── Original 1-on-1 handlers (kept for backward compat, now redirect to new logic) ──
  const handleQuestionAction = useCallback(async () => {}, []);
  const loadInterviewQuestions = useCallback(async () => {}, []);

  // ── Candidate interview timer effect (legacy, not used in group mode) ──
  useEffect(() => {
    isInterviewEndedByAdminRef.current = false;
    if (timers.current.candidate) clearTimeout(timers.current.candidate);
    if (timers.current.candidateInterval) clearInterval(timers.current.candidateInterval);
    setShowCandidateQuestions(false);
    setCurrentQuestion(null);
    if (setAskedQuestionIds) setAskedQuestionIds([]);
    if (askedQuestionsRef) askedQuestionsRef.current = [];
    setWaitingForNext(false);
    setNextQuestionTime(null);
    setInterviewCompleted(false);
    return () => {
      if (timers.current.candidate) clearTimeout(timers.current.candidate);
      if (timers.current.candidateInterval) clearInterval(timers.current.candidateInterval);
    };
  }, [currentInterviewForCandidate?.interviewId]);

  // ── Poll for pending interview (candidate side) ──────────────
  const checkPendingInterviewQuestions = useCallback(async () => {
    if (!setIsCheckingInterview || isCheckingInterview || showCandidateQuestions ||
        showInterviewConfirm || waitingForNext || interviewCompleted || currentInterviewForCandidate) return;
    setIsCheckingInterview(true);
    try {
      const { data } = await axios.get(
        `${config.baseURL}/api/teams/user/active-interview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.success && data?.active_interview && !currentInterviewForCandidate) {
        setPendingInterview(data.active_interview);
        setShowInterviewConfirm(true);
      }
    } catch (err) {
      console.error('Error checking pending questions:', err);
    } finally {
      setIsCheckingInterview(false);
    }
  }, [token, setIsCheckingInterview, isCheckingInterview, showCandidateQuestions,
      showInterviewConfirm, waitingForNext, interviewCompleted,
      currentInterviewForCandidate, setPendingInterview, setShowInterviewConfirm]);

  return {
    loadAvailableQuestions,
    checkActiveInterview,
    checkIfUserInActiveInterview,
    checkPendingInterviewQuestions,
    loadInterviewQuestions,
    handleQuestionAction,
    logCandidateQuestion,
    askCandidateQuestion,
    handleStartInterview,
    handleEndInterview,
    getNextQuestion,
  };
};